import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Tooltip from './Tooltip';

interface Service {
  number: string;
  icon: string;
  title: string;
  description: string | React.ReactNode;
  action: string | React.ReactNode;
}

const ServicesSection: React.FC = () => {
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());

  const services: Service[] = [
    {
      number: "01",
      icon: "/images/icons/biodiversity.png",
      title: "Marine Biodiversity Assessment",
      description: "Advanced biodiversity surveys using metabarcoding and metagenomics for state-of-the-art species inventories, environmental impact assessments, baseline studies, and marine spatial planning.",
      action: "Capture the full spectrum of marine life that traditional surveys miss."
    },
    {
      number: "02",
      icon: "/images/icons/metabolism.png",
      title: "Ocean Health & Conservation Performance Analytics",
      description: "Real-time ecosystem health scoring revealing biodiversity, functional diversity, threats, and opportunities through genomic intelligence.",
      action: "Track conservation effectiveness over time, transforming complex data into standardized metrics for adaptive management and regulatory compliance."
    },
    {
      number: "03",
      icon: "/images/icons/kg.png",
      title: "Ecosystem Intelligence & Modeling",
      description: "Time-series analysis, ecological network mapping, and food web reconstruction to understand ecosystem dynamics and species interactions.",
      action: "Reveal trophic relationships, community structure, and ecosystem connectivity patterns to inform ecosystem-based fisheries management, climate adaptation strategies, and Marine Protected Area design."
    },
    {
      number: "04",
      icon: "/images/icons/metagenomics.png",
      title: "Biodiversity Verification Service",
      description: "Standardized biodiversity uplift quantification and permanence risk scoring for Monitoring, Reporting, and Verification (MRV) compliance supporting biodiversity credits, carbon markets, and nature-based finance instruments.",
      action: "Transforms conservation outcomes into financial market standards for MRV compliance."
    },
    {
      number: "05",
      icon: "/images/icons/bioprospecting.png",
      title: "Marine Genetic Resource Discovery",
      description: (
        <>
          Comprehensive <Tooltip definition="Bioprospecting is the search for commercially valuable products by analyzing the genetic material (genomes) of organisms found in nature, especially microbes. This process can reveal novel genes, enzymes, peptides, or pathways with applications in biotechnology, bioremediation, medicine, and industry." term="Bioprospecting">bioprospecting</Tooltip> identifying novel <Tooltip definition="Biosynthetic Gene Clusters (BGCs) are groups of genes that collectively encode enzymes producing specialized metabolites, including antibiotics, antitumor agents, and other commercially valuable compounds." term="Biosynthetic Gene Clusters">biosynthetic gene clusters</Tooltip>, bioactive peptides, and commercially valuable enzymes.
        </>
      ),
      action: (
        <>
          Transform ocean biodiversity into non-extractive and sustainable revenue streams with full <Tooltip definition="Benefit-sharing compliance refers to adhering to international and national frameworks, foremost the Nagoya Protocol, that regulate access to genetic resources and ensure that benefits arising from their use (scientific, financial, or technological) are shared fairly and equitably with the country or community of origin." term="Benefit-sharing Compliance">benefit-sharing compliance</Tooltip>.
        </>
      )
    },
    {
      number: "06",
      icon: "/images/icons/dashboard.png",
      title: "Integrated Data Platform & Dashboards",
      description: (
        <>
          Unified platform combining <Tooltip definition="A Knowledge Graph is a semantic data structure that represents information as a network of entities (nodes) and their relationships (edges), enabling rich, flexible connections across diverse domains. Unlike traditional relational databases, which use fixed tables and predefined joins, knowledge graphs encode meaningful, contextual relationships—such as 'Gene A is expressed by Species B,' or 'Location X contains Sampling Station Y'—in a graph-based format that supports inference, querying, and reasoning." term="Knowledge Graphs">knowledge graphs</Tooltip> with near-real-time monitoring.
        </>
      ),
      action: "Seamless multi-source data fusion (genomic, sensor, satellite, environmental) with natural language AI queries and intuitive graphics for adaptive conservation management."
    },
    {
      number: "07",
      icon: "/images/icons/sustainable_finance.png",
      title: "Self-Sustaining Conservation Finance",
      description: "Ocean BioMetrics creates the world's first regenerative ocean conservation model. We transform marine biodiversity into multiple revenue streams—biodiversity credits, carbon markets, and genetic asset licensing—that directly fund expanded ocean protection.",
      action: "The more biodiversity we discover and protect, the more revenues are generated to protect even more. Our model also ensures local communities and nations benefit fairly from their ocean resources while meeting international standards for marine governance and benefit-sharing, establishing conservation finance that sustains long-term ocean protection and management."
    }
  ];

  const toggleExpanded = (index: number) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <section id="services" className="py-5" style={{
      background: `
        linear-gradient(-90deg, rgba(20, 25, 30, 0.9) 0%, rgba(30, 35, 40, 0.9) 40%, rgba(45, 52, 58, 0.9) 100%),
        url('/images/background-images/funnyfish.jpg')
      `,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      position: 'relative'
    }}>
      <style>
        {`
          @keyframes pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(24, 195, 186, 0.4);
            }
            70% {
              box-shadow: 0 0 0 15px rgba(24, 195, 186, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(24, 195, 186, 0);
            }
          }
          @keyframes bounce {
            0%, 20%, 50%, 80%, 100% {
              transform: translateY(0);
            }
            40% {
              transform: translateY(-5px);
            }
            60% {
              transform: translateY(-3px);
            }
          }
        `}
      </style>
      <div className="container">
        <div className="row justify-content-center mb-5">
          <div className="col-lg-8 text-center">
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 700,
              marginBottom: '1rem',
              letterSpacing: '-0.02em',
              lineHeight: 1.3,
              color: '#EFEFE3',
              fontFamily: "'Montserrat', sans-serif"
            }}>
              What We Offer
            </h2>
            <p style={{
              fontSize: '1.1rem',
              color: '#D5D6CC',
              lineHeight: '1.6',
              marginBottom: '1rem'
            }}>
              Our integrated platform combines advanced genomic analysis, AI-powered modeling, and multi-source data fusion to deliver comprehensive ocean intelligence. These core capabilities power all our solutions.
            </p>
          </div>
        </div>

        {/* Responsive Grid for Service Cards */}
        <div className="row g-4 justify-content-center">
          {services.map((service, index) => {
            const isExpanded = expandedCards.has(index);

            return (
              <div key={index} className="col-lg-4 col-md-6">
                <div className="service-card" style={{
                  background: 'rgba(239, 239, 227, 0.04)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(239, 239, 227, 0.08)',
                  borderRadius: '15px',
                  boxShadow: isExpanded
                    ? '0 20px 50px rgba(8, 136, 208, 0.2)'
                    : '0 10px 40px rgba(0,0,0,0.25)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  height: '100%',
                  borderColor: isExpanded
                    ? 'rgba(8, 136, 208, 0.3)'
                    : 'rgba(239, 239, 227, 0.08)'
                }}>

                  {/* Clickable Header Section */}
                  <div
                    onClick={() => toggleExpanded(index)}
                    style={{
                      padding: '1.25rem 1.25rem 0.75rem',
                      cursor: 'pointer',
                      transition: 'background 0.3s ease',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      if (!isExpanded) {
                        e.currentTarget.style.background = 'rgba(239, 239, 227, 0.02)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    {/* Icon and Number */}
                    <div className="d-flex align-items-center justify-content-center mb-2" style={{
                      height: '80px',
                      position: 'relative'
                    }}>
                      {service.icon && service.icon !== "/images/icons/placeholder.png" ? (
                        <img
                          src={service.icon}
                          alt={`${service.title} Icon`}
                          style={{
                            width: '70px',
                            height: '70px',
                            filter: 'brightness(0) invert(1)',
                            opacity: 0.85,
                            objectFit: 'cover',
                            objectPosition: 'center'
                          }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div style={{
                        width: '70px',
                        height: '70px',
                        background: 'rgba(8, 136, 208, 0.2)',
                        borderRadius: '12px',
                        display: service.icon && service.icon !== "/images/icons/placeholder.png" ? 'none' : 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.8rem',
                        fontWeight: 700,
                        color: '#0888D0',
                        fontFamily: "'Montserrat', sans-serif"
                      }}>
                        {service.number}
                      </div>
                    </div>

                    {/* Title */}
                    <h3 style={{
                      color: '#EFEFE3',
                      fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)',
                      fontWeight: 700,
                      fontFamily: "'Montserrat', sans-serif",
                      lineHeight: '1.3',
                      margin: '0 0 0.5rem 0',
                      textAlign: 'center',
                      minHeight: '45px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {service.title}
                    </h3>

                    {/* Expand/Collapse Icon */}
                    <div style={{
                      position: 'absolute',
                      top: '1.5rem',
                      right: '1.5rem',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      background: isExpanded
                        ? 'rgba(8, 136, 208, 0.15)'
                        : 'rgba(239, 239, 227, 0.05)',
                      border: isExpanded
                        ? '1px solid rgba(8, 136, 208, 0.3)'
                        : '1px solid rgba(239, 239, 227, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease'
                    }}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 16 16"
                        fill="none"
                        style={{
                          transition: 'transform 0.3s ease',
                          transform: isExpanded ? 'rotate(45deg)' : 'rotate(0deg)'
                        }}
                      >
                        <path
                          d="M8 3V13M3 8H13"
                          stroke={isExpanded ? '#0888D0' : '#D5D6CC'}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Expandable Description */}
                  <div style={{
                    maxHeight: isExpanded ? '600px' : '0',
                    opacity: isExpanded ? 1 : 0,
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      padding: '1rem 1.25rem 1.5rem',
                      transform: isExpanded ? 'translateY(0)' : 'translateY(-10px)',
                      transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      borderTop: '1px solid rgba(239, 239, 227, 0.08)',
                      marginTop: '0.5rem'
                    }}>
                      <div>
                        <p style={{
                          color: '#D5D6CC',
                          fontSize: '1rem',
                          lineHeight: '1.6',
                          margin: '0 0 1rem 0',
                          textAlign: 'left',
                          opacity: 0.9
                        }}>
                          {service.description}
                        </p>

                        {service.action && (
                          <>
                            <div style={{
                              display: 'flex',
                              justifyContent: 'center',
                              margin: '1.25rem 0'
                            }}>
                              <div style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, rgba(24, 195, 186, 0.15), rgba(8, 136, 208, 0.15))',
                                border: '2px solid #18C3BA',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                animation: 'pulse 2s infinite'
                              }}>
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  style={{
                                    animation: 'bounce 1.5s infinite'
                                  }}
                                >
                                  <path
                                    d="M12 5V19M12 19L5 12M12 19L19 12"
                                    stroke="#18C3BA"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            </div>

                            <p style={{
                              color: '#D5D6CC',
                              fontSize: '1rem',
                              lineHeight: '1.6',
                              margin: '0',
                              textAlign: 'left',
                              opacity: 0.9
                            }}>
                              {service.action}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bottom Accent Line */}
                  <div style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    background: 'linear-gradient(90deg, rgba(8, 136, 208, 0.6), rgba(8, 136, 208, 0))',
                    opacity: isExpanded ? 1 : 0,
                    transition: 'opacity 0.3s ease'
                  }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Call to Action - Link to Resources */}
        <div className="text-center mt-5 pt-4">
          <p className="mb-3" style={{ fontSize: '1.1rem', color: '#D5D6CC' }}>
            Explore our computational tools and datasets
          </p>
          <Link
            to="/data-apps"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem 2.5rem',
              background: 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(24, 195, 186, 0.3)',
              borderRadius: '50px',
              color: '#18C3BA',
              textDecoration: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              fontFamily: "'Montserrat', sans-serif",
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 8px 25px rgba(24, 195, 186, 0.15)',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.25) 0%, rgba(24, 195, 186, 0.35) 100%)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(24, 195, 186, 0.25)';
              e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'linear-gradient(135deg, rgba(24, 195, 186, 0.15) 0%, rgba(24, 195, 186, 0.25) 100%)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(24, 195, 186, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(24, 195, 186, 0.3)';
            }}
          >
            <span>Resources</span>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                transition: 'transform 0.3s ease'
              }}
            >
              <path
                d="M7 17L17 7M17 7H7M17 7V17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Link>
        </div>

      </div>
    </section>
  );
};

export default ServicesSection;